import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { setAuthToken } from '../../utils/auth';
import { loginUser } from '../../services/apiService';
import { getRelabelingRecordsByEmail } from '../../services/apiService'; // Import the API function

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [error, setError] = useState('');

  function validatePassword(password) {
    const uppercase = /[A-Z]/.test(password);
    const lowercase = /[a-z]/.test(password);
    const specialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    if (uppercase && lowercase && specialChar) {
      return false; // Consider updating logic to return true for valid passwords
    } else {
      return true; // Password is weak
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === "email" ? value.toLowerCase() : value
    });
  };

  const togglePasswordVisibility = () => {
    setIsPasswordVisible((prev) => !prev);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const data = await loginUser(formData);
      if (data.error) {
        setError(data.error);
        return;
      }

      setAuthToken(data.token);
      const isWeakPassword = validatePassword(formData.password);
      sessionStorage.setItem("isWeakPassword", isWeakPassword);
      localStorage.setItem("email", formData.email);

      // Call getRelabelingRecordsByEmail after successful login
      let relabelingData = [];
      try {
        const relabelingResponse = await getRelabelingRecordsByEmail(formData.email);
        relabelingData = relabelingResponse.data || [];
      } catch (error) {
        console.error('Error fetching relabeling data:', error);
        // Optionally handle the error (e.g., proceed without relabeling data)
      }

      if (data.isPasswordUpdated === false) {
        navigate(`/change-password/${formData.email}`);
      } else {
        navigate('/dashboard', {
          state: { relabelingProducts: relabelingData } // Pass relabeling data to dashboard
        });
      }
    } catch (error) {
      setError(error.message || 'Login failed');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white p-4">
      <button className="mb-6 cursor-pointer" onClick={() => navigate(-1)}>
        <ArrowLeft className="w-6 h-6" />
      </button>

      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-semibold mb-6">Login</h1>

        {error && (
          <div className="bg-red-500 text-white p-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your login ID"
              className="w-full bg-gray-800 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="relative">
            <input
              type={isPasswordVisible ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              className="w-full bg-gray-800 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-black focus:outline-none cursor-pointer"
            >
              {isPasswordVisible ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors cursor-pointer"
          >
            Log in
          </button>
        </form>

        <p className="text-sm text-gray-400 mt-4">
          Please contact your TPV window if you have any login problems.
        </p>
      </div>
    </div>
  );
};

export default Login;